<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import { EyeIcon, GlobeIcon, ExternalIcon, RefreshIcon } from '@/icons'
import { getBadgeStatus } from '@/utils/badgeUtils'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import type { AxiosError } from 'axios'
import router from '@/router'

import EmptyState from '@/components/common/EmptyState.vue'
import EnvelopeIcon from '@/icons/EnvelopeIcon.vue'
import MobileIcon from '@/icons/MobileIcon.vue'

// Define interface for consignee data
interface Consignee {
  id: number
  name: string
  designation: string
  number: string
  emailId: string
  faxNumber: string
  callAlignedDate: string
  assignedEngineerId: string
  assignedEngineerName: string
  engineerEmail?: string
  engineerPhone?: string
  isCompleted?: boolean
  completionNotes?: string
  completedAt?: string
  installationStatus: string
  totalItems: number
  installedItems: number
  reportCards: Array<{
    id: number
    title: string
    itemName: string
    quantity: number
    visibleFields: Record<string, boolean>
    itemGroups: Array<{
      fieldValues: Record<string, string>
    }>
  }>
}

// Define interface for API response
interface EngineerConsigneeResponse {
  tenderId: string
  t247Id: string
  referenceNo: string
  tenderBrief: string
  organization: string
  location: string
  sheetType: string
  workflowStatus: string
  consignee: Consignee
}

// Define interface for tender with consignees
interface TenderWithConsignees {
  id: string
  t247Id: string
  referenceNo: string
  tenderBrief: string
  tenderBriefLink?: string
  organization: string
  location: string
  sheetType: string
  workflowStatus: string
  consignees: Consignee[]
}

const tenders = ref<TenderWithConsignees[]>([])
const searchFilter = ref('')
const loading = ref(false)
const error = ref<string | null>(null)

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Fetch engineer consignees from the new endpoint
const fetchEngineerConsignees = async () => {
  try {
    loading.value = true
    error.value = null

    // Use the new endpoint that returns consignee data
    const response = await api.get('/api/engineer-consignees')

    if (response.data.success) {
      const data: EngineerConsigneeResponse[] = response.data.data
      console.log('📊 Engineer consignees data:', data)

      // Group by tender to display as before
      const tenderMap = new Map<string, TenderWithConsignees>()

      for (const item of data) {
        const tenderId = item.tenderId
        if (!tenderMap.has(tenderId)) {
          tenderMap.set(tenderId, {
            id: tenderId,
            t247Id: item.t247Id,
            referenceNo: item.referenceNo,
            tenderBrief: item.tenderBrief,
            tenderBriefLink: '',
            organization: item.organization,
            location: item.location,
            sheetType: item.sheetType,
            workflowStatus: item.workflowStatus,
            consignees: []
          })
        }

        const tender = tenderMap.get(tenderId)!
        tender.consignees.push(item.consignee)
      }

      tenders.value = Array.from(tenderMap.values())
      console.log(`✅ Loaded ${tenders.value.length} tenders with consignee data`)
    } else {
      error.value = response.data.error || 'Failed to load engineer consignees'
    }
  } catch (err: unknown) {
    console.error('Error fetching engineer consignees:', err)
    error.value = getAxiosErrorMessage(err as AxiosError)
  } finally {
    loading.value = false
  }
}

// Filtered tenders based on search
const filteredTenders = computed(() => {
  if (!searchFilter.value) return tenders.value

  const searchTerm = searchFilter.value.toLowerCase()
  return tenders.value.filter(
    (tender) =>
      tender.t247Id?.toLowerCase().includes(searchTerm) ||
      tender.referenceNo?.toLowerCase().includes(searchTerm) ||
      tender.tenderBrief?.toLowerCase().includes(searchTerm) ||
      tender.organization?.toLowerCase().includes(searchTerm) ||
      tender.location?.toLowerCase().includes(searchTerm) ||
      tender.consignees?.some(c =>
        c.name.toLowerCase().includes(searchTerm) ||
        c.assignedEngineerName.toLowerCase().includes(searchTerm)
      )
  )
})

const viewTenderDetails = (tender: TenderWithConsignees) => {
  router.push({
    path: `/tenders/${tender.t247Id}`,
    query: { referrer: 'engineer-tenders' },
  })
}

// Get consignee status badge severity
// const getConsigneeStatusSeverity = (status: string): string => {
//   const severityMap: Record<string, string> = {
//     completed: 'success',
//     'in-progress': 'info',
//     pending: 'warning',
//   }
//   return severityMap[status] || 'secondary'
// }

// Get consignee status label
// const getConsigneeStatusLabel = (status: string): string => {
//   const labelMap: Record<string, string> = {
//     completed: 'Completed',
//     'in-progress': 'In Progress',
//     pending: 'Pending',
//   }
//   return labelMap[status] || status
// }

// Clear filters
const clearFilters = () => {
  searchFilter.value = ''
}

// Refresh data
const refreshData = async () => {
  await fetchEngineerConsignees()
}

// Lifecycle
onMounted(async () => {
  await fetchEngineerConsignees()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Engineer Tenders" />

    <!-- Error Message -->
    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-800">{{ error }}</p>
          <button
            @click="refreshData"
            class="mt-1 text-sm text-red-700 hover:text-red-600 underline"
          >
            Click here to retry
          </button>
        </div>
      </div>
    </div>

    <!-- Tender List Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Engineer Tender Details"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex items-center justify-between gap-2">
          <!-- Search Input -->
          <TextInputDefault
            v-model="searchFilter"
            placeholder="Search by tender, consignee, engineer..."
            id="searchengineertenders"
            name="searchengineertenders"
            type="text"
            size="xss"
          />

          <!-- Action Buttons -->
          <div class="flex gap-2 ml-auto">
            <BaseButton
              v-if="searchFilter"
              text="Clear Filters"
              size="xss"
              variant="ghost"
              @click="clearFilters"
            />

            <BaseButton
              size="xss"
              :icon="RefreshIcon"
              @click="refreshData"
              :loading="loading"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i
            class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"
          ></i>
          Loading engineer tender data...
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="w-full flex flex-col items-center text-center py-8 gap-3 text-red-500"
        >
          <p>{{ error }}</p>
          <BaseButton text="Retry" size="sm" @click="refreshData" class="w-30" />
        </div>

        <!-- No Data State -->
        <EmptyState
          v-else-if="tenders.length === 0"
          :icon="GlobeIcon"
          title="No engineer assignments"
          description="No consignees have been assigned to engineers yet."
          hint="Engineers will appear here once admins assign consignees in the Reports Management page"
        />

        <!-- No Results after Filtering -->
        <EmptyState
          v-else-if="filteredTenders.length === 0"
          :icon="GlobeIcon"
          title="No matching records"
          description="No records match your search criteria."
          action-text="Clear Filters"
          @action="clearFilters"
        />

        <!-- Tender Table -->
        <DataTable
          v-else-if="filteredTenders.length > 0"
          :value="filteredTenders"
          class="w-full"
          sortMode="multiple"
          scrollable
          scrollHeight="450px"
          resizableColumns
          columnResizeMode="fit"
          showGridlines
          :pt="{
            table: { style: { borderCollapse: 'separate', borderSpacing: '0' } },
            thead: { style: { position: 'sticky', top: '0', zIndex: '20' } },
            tbody: { style: { position: 'relative', zIndex: '10' } },
          }"
        >
          <!-- Tender ID Column -->
          <Column
            sortable
            frozen
            field="referenceNo"
            header="Reference No"
            headerStyle="position: sticky; left: 48px; z-index: 30;"
            bodyStyle="position: sticky; left: 48px; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex flex-col max-w-[100px] text-xs">
                <a
                  :href="data.tenderBriefLink"
                  target="_blank"
                  class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal"
                  v-if="data.tenderBriefLink"
                >
                  {{ data.referenceNo }}
                  <ExternalIcon class="w-3 h-3 text-green-600 flex-shrink-0" />
                </a>
                <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
              </div>
            </template>
          </Column>

          <!-- Consignees Column -->
          <Column field="consignees" header="Consignees">
            <template #body="{ data }">
              <div v-if="data.consignees && data.consignees.length > 0" class="space-y-1">
                <div v-for="consignee in data.consignees" :key="consignee.id" class="border-b pb-3 last:border-0">
                  <div class="flex flex-col items-center justify-between mb-2">
                      <span class="text-xs font-medium">{{ consignee.name }}</span>
                      <span class="text-xs ml-2">({{ consignee.designation }})</span>
                  </div>
                </div>
              </div>
              <span v-else class="text-sm text-gray-400">No consignees assigned</span>
            </template>
          </Column>

          <Column field="consignees" header="Assigned Engineers">
            <template #body="{ data }">
              <div v-if="data.consignees && data.consignees.length > 0" class="space-y-3">
                <div v-for="consignee in data.consignees" :key="consignee.id" class="border-b pb-3 last:border-0">
                  <!-- Engineer Info -->
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium">
                        {{ consignee.assignedEngineerName || 'Not Assigned' }}
                      </span>
                    </div>

                    <div v-if="consignee.engineerEmail" class="flex items-center gap-1 group">
                      <EnvelopeIcon class="w-4 h-4" />
                      <a
                        :href="`mailto:${consignee.engineerEmail}`"
                        class="truncate hover:text-blue-600 hover:underline transition-colors text-xs"
                        :title="`Email: ${consignee.engineerEmail}`"
                      >
                        {{ consignee.engineerEmail }}
                      </a>
                    </div>
                    <div v-if="consignee.engineerPhone" class="flex items-center gap-1 group">
                      <MobileIcon class="w-4 h-4" />
                      <a
                        :href="`tel:${consignee.engineerPhone}`"
                        class="truncate hover:text-green-600 hover:underline transition-colors text-xs"
                        :title="`Phone: ${consignee.engineerPhone}`"
                      >
                        {{ consignee.engineerPhone }}
                      </a>
                    </div>


                  <!-- Progress Bar -->
                  <!-- <div class="text-xs mt-1">
                    Progress: {{ consignee.installedItems }}/{{ consignee.totalItems }} items
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                    <div
                      class="bg-green-600 h-1.5 rounded-full"
                      :style="{ width: `${consignee.totalItems > 0 ? Math.round((consignee.installedItems / consignee.totalItems) * 100) : 0}%` }"
                    ></div>
                  </div> -->

                  <!-- Completed Info -->
                  <div v-if="consignee.isCompleted" class="text-xs text-green-600 mt-1">
                    ✓ Completed on {{ formatDate(consignee.completedAt || '') }}
                  </div>
                </div>
              </div>
              <span v-else class="text-sm text-gray-400">No consignees assigned</span>
            </template>
          </Column>

          <!-- Type Column -->
          <Column sortable field="sheetType" header="Type">
            <template #body="{ data }">
              <Badge
                :value="data.sheetType"
                :severity="getBadgeStatus(data.sheetType?.toLowerCase()) || 'secondary'"
              />
            </template>
          </Column>

          <!-- Organization Column -->
          <Column sortable field="organization" header="Organization">
            <template #body="{ data }">
              <div class="text-xs">{{ data.organization }}</div>
            </template>
          </Column>

          <!-- Location Column -->
          <Column sortable field="location" header="Location">
            <template #body="{ data }">
              <span class="text-xs">{{ data.location }}</span>
            </template>
          </Column>

          <!-- Tender Status Column -->
          <Column sortable field="workflowStatus" header="Tender Status">
            <template #body="{ data }">
              <Badge
                :value="data.workflowStatus === 'won' ? 'Won' : data.workflowStatus"
                :class="'p-badge-' + getBadgeStatus(data.workflowStatus)"
              />
            </template>
          </Column>

          <!-- Action Column -->
          <Column
            frozen
            field="action"
            header="Actions"
            headerStyle="width: 60px; position: sticky; right: 0; z-index: 30;"
            bodyStyle="position: sticky; right: 0; z-index: 25;"
          >
            <template #body="{ data }">
              <div class="flex gap-2">
                <BaseButton
                  size="xss"
                  :aria-label="`view ${data.t247Id} details`"
                  @click="viewTenderDetails(data)"
                  variant="secondary"
                  :icon="EyeIcon"
                  tooltip="View Tender Details"
                  tooltipPosition="bottom"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>

      <template #footer v-if="!loading && !error && filteredTenders.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredTenders.length }} of {{ tenders.length }} tenders with engineer assignments
            <span v-if="searchFilter">(filtered)</span>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span>Last updated: {{ new Date().toLocaleTimeString() }}</span>
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>
</template>
